import { createContext, useEffect } from 'react';

import { useDialogs } from '@toolpad/core/useDialogs';
import { useNavigate } from 'react-router';

import { GetStudentResponse, useGetStudent } from 'api/hooks/private/users/students/GetStudent';
import {
  StudentCourseEnrollment,
  useGetStudentCourseEnrollments,
} from 'api/hooks/private/users/students/GetStudentCourseEnrollments';
import { LedgerEntry, useGetStudentLedger } from 'api/hooks/private/users/students/GetStudentLedger';
import {
  StudentPeriodEnrollment,
  useGetStudentPeriodEnrollments,
} from 'api/hooks/private/users/students/GetStudentPeriods';

import useAuthContext from 'hooks/useAuthContext';

import { formatCents } from 'utils/formatCurrency';

interface IStudentContext {
  loadingStudent: boolean;
  student?: GetStudentResponse | null;
  loadingCourseEnrollments: boolean;
  courseEnrollments: StudentCourseEnrollment[];
  loadingPeriodEnrollments: boolean;
  periodEnrollments: StudentPeriodEnrollment[];
  loadingLedger: boolean;
  ledger?: LedgerEntry[] | null;
}

// create context with default values
const StudentContext = createContext<IStudentContext>({
  loadingStudent: true,
  student: null,
  loadingCourseEnrollments: true,
  courseEnrollments: [],
  loadingPeriodEnrollments: true,
  periodEnrollments: [],
  loadingLedger: true,
  ledger: [],
});

interface PropTypes {
  children: JSX.Element;
}

function StudentContextProvider({ children }: PropTypes) {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const dialogs = useDialogs();
  const navigate = useNavigate();

  // fetch student information
  const { data: student, isLoading: loadingStudent } = useGetStudent({
    variables: {
      id: studentId ?? '',
    },
  });

  // fetch student course enrollments
  const { data: courseEnrollments, isLoading: loadingCourseEnrollments } = useGetStudentCourseEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });

  // fetch student period enrollments
  const { data: periodEnrollments, isLoading: loadingPeriodEnrollments } = useGetStudentPeriodEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });

  const { data: ledger, isLoading: loadingLedger } = useGetStudentLedger({
    variables: {
      id: studentId ?? '',
      showVoided: false,
    },
    enabled: !!studentId,
  });

  useEffect(() => {
    const balance = Number(ledger?.[ledger.length - 1]?.balance ?? 0);
    if (balance > 0) {
      const studentBalance = formatCents(balance);

      // alert user of the non-zero balance with an option to view the ledger
      dialogs
        .confirm(`You have a balance of ${studentBalance}`, {
          okText: 'View Ledger',
          cancelText: 'Dismiss',
        })
        .then((confirmed) => {
          if (confirmed) {
            navigate('/student/ledger');
          }
        });
      console.log(`You have a balance of ${studentBalance}`);
    }
  }, [ledger, dialogs, navigate]);

  return (
    <StudentContext.Provider
      value={{
        loadingStudent: loadingStudent,
        student: student,
        loadingCourseEnrollments,
        courseEnrollments: courseEnrollments ?? [],
        loadingPeriodEnrollments,
        periodEnrollments: periodEnrollments ?? [],
        loadingLedger,
        ledger: ledger ?? [],
      }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export type { IStudentContext };
export { StudentContext, StudentContextProvider };
